import { useState, useEffect, useCallback } from "react";
import API from "../lib/api";
import { Button } from "./ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Play, Stop, Timer as TimerIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

function elapsed(startedAt, nowTick) {
  const secs = Math.max(0, Math.floor((nowTick - new Date(startedAt).getTime()) / 1000));
  const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), s = secs % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}` : `${m}:${String(s).padStart(2, "0")}`;
}

/* Header task timer: start/stop work timers against projects & tasks. */
export default function TimerWidget() {
  const [timer, setTimer] = useState(null);
  const [open, setOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [taskId, setTaskId] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(Date.now());

  const fetchCurrent = useCallback(() => {
    API.get("/monitoring/timer/current").then(({ data }) => setTimer(data.timer)).catch(() => {});
  }, []);
  useEffect(() => { fetchCurrent(); }, [fetchCurrent]);

  useEffect(() => {
    if (!timer) return;
    const iv = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [timer]);

  useEffect(() => {
    if (!open) return;
    API.get("/projects").then(({ data }) => {
      const list = Array.isArray(data) ? data : data.projects || [];
      setProjects(list.map(p => ({ id: p.id, name: p.name })));
    }).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!projectId) { setTasks([]); setTaskId(""); return; }
    API.get(`/tasks?project_id=${projectId}`).then(({ data }) => {
      const list = Array.isArray(data) ? data : data.tasks || [];
      setTasks(list.map(t => ({ id: t.id, title: t.title })));
    }).catch(() => setTasks([]));
  }, [projectId]);

  const start = async () => {
    setBusy(true);
    try {
      const project = projects.find(p => p.id === projectId);
      const task = tasks.find(t => t.id === taskId);
      const { data } = await API.post("/monitoring/timer/start", {
        project_id: projectId || "", project_name: project?.name || "",
        task_id: taskId || "", task_title: task?.title || "", label: label.trim(),
      });
      setTimer(data);
      setOpen(false);
      setLabel("");
      toast.success("Timer started");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to start timer"); }
    finally { setBusy(false); }
  };

  const stop = async () => {
    setBusy(true);
    try {
      const { data } = await API.post("/monitoring/timer/stop");
      setTimer(null);
      toast.success(`Timer stopped — ${data.duration_minutes} min logged${data.task_title ? ` on ${data.task_title}` : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to stop timer"); }
    finally { setBusy(false); }
  };

  if (timer) {
    return (
      <button onClick={stop} disabled={busy} title={`${timer.task_title || timer.project_name || timer.label || "Work timer"} — click to stop`}
        className="flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1 text-[11px] font-semibold transition-colors"
        data-testid="timer-running-btn">
        <Stop size={11} weight="fill" />
        <span className="font-mono">{elapsed(timer.started_at, tick)}</span>
        <span className="hidden sm:inline max-w-[110px] truncate font-normal opacity-90">{timer.task_title || timer.project_name || timer.label || ""}</span>
      </button>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="text-muted-foreground hover:text-foreground" title="Start a work timer" data-testid="timer-widget-btn">
          <TimerIcon size={20} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 space-y-3" data-testid="timer-popover">
        <p className="text-xs font-semibold">Start a work timer</p>
        <div>
          <Label className="text-[10px]">Project</Label>
          <Select value={projectId || undefined} onValueChange={setProjectId}>
            <SelectTrigger className="h-8 text-xs" data-testid="timer-project-select"><SelectValue placeholder="Select project (optional)" /></SelectTrigger>
            <SelectContent>
              {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {tasks.length > 0 && (
          <div>
            <Label className="text-[10px]">Task</Label>
            <Select value={taskId || undefined} onValueChange={setTaskId}>
              <SelectTrigger className="h-8 text-xs" data-testid="timer-task-select"><SelectValue placeholder="Select task (optional)" /></SelectTrigger>
              <SelectContent>
                {tasks.map(t => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label className="text-[10px]">Or describe the work</Label>
          <Input value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Chasing supplier quotes" className="h-8 text-xs" data-testid="timer-label-input" />
        </div>
        <Button size="sm" className="w-full h-8" onClick={start} disabled={busy || (!projectId && !label.trim())} data-testid="timer-start-btn">
          <Play size={13} className="mr-1" weight="fill" /> Start Timer
        </Button>
      </PopoverContent>
    </Popover>
  );
}
